// PLATFORM-1 — the per-account navigation layer.
//
// THE GOVERNING RULE: THE ACCOUNT LAYER MAY ONLY SUBTRACT.
// Resolved visibility is roleGate AND accountEnables. It is enforced by
// CONSTRUCTION here, not by discipline: applyAccountLayer receives a tree that
// has ALREADY been role-filtered, and it can only remove from it. There is no
// code path that reads the raw SECTIONS constant, so there is nothing for an
// override to add a node back from. A test tries to switch owner-only rows on
// for a technician and asserts none of them appears.
//
// THE SECOND GOVERNING RULE: an account that has configured nothing renders
// BYTE IDENTICAL to today. An empty config returns the input array's contents
// unchanged - same order, same labels, same routes - and a test compares the
// two trees to prove it.
//
// WHY IDS AND NOT ROUTES. A child used to be identified only by its route, so
// an override keyed on a route silently orphans the moment the route changes
// and the merchant's hidden module quietly comes back. PLATFORM-1 gave every
// child a stable id derived from its purpose. Nothing here ever looks at a
// route to decide what a merchant meant.

import type { NavChild, NavSection } from './navConfig'

/** One merchant's decision about one node. */
export interface NavNodeOverride {
  hidden: boolean
  /** Their own word for it, or null to keep ours. */
  label: string | null
  /** Position within its own level, or null to keep ours. */
  sortOrder: number | null
}

/** Keyed by node id. A node with no entry is untouched. */
export type AccountNavConfig = Record<string, NavNodeOverride>

/**
 * PERMANENT MODULES. These can never be hidden, and the attempt is REFUSED
 * rather than silently ignored - both here and in the database, which rejects
 * the row outright.
 *
 * Each is load-bearing for something other than itself: Settings is where a
 * hidden module is turned back on, so hiding it would be a one-way door. Team
 * and Money carry obligations to people and to money that a business does not
 * get to stop seeing. Home is where every "you are here" lands.
 *
 * ONE-GHOST-2: the 'servicebot' node no longer opens a second assistant — it
 * opens the APPROVAL QUEUE, and it is permanent for a stronger reason than
 * before. Drafts addressed to real customers wait there; a business does not
 * get to hide the place its outgoing messages are approved. Ghost himself is
 * the launcher, which renders on every screen and is not a nav node at all,
 * so asking for help can no longer be hidden by any means.
 */
export const PERMANENT_SECTION_IDS: ReadonlyArray<string> = [
  'home',
  'settings',
  // APPLY-3B: 'team' left this list. It was on for every account including
  // solo operators, which is the clearest violation of the principle that a
  // section must be a place the business does work. Settings stays, so the way
  // back is always one click. Mirrors the database constraint exactly.
  //
  // THE-BUILDER-CAN-SWITCH-A-SECTION-ON-1: MONEY AND CONNECTORS LEFT IT, and
  // the database went first. `20261247` narrowed
  // `account_navigation_permanent_not_hidden` to the ruled furniture and the
  // owner's Claude applied it; read back live on 2026-09-17 the check names
  // home, settings, settings_menu, automations and automations_automations and
  // nothing else. This list mirrors that constraint, so it says the same five.
  //
  // The wallet and the bill did not go with Money: they are Settings children
  // now, and Settings is permanent, so a merchant can never be billed with no
  // screen that shows the bill.
  //
  // AGENTS-RENAME-1: the node id stays 'automations' on purpose. Only the LABEL
  // becomes Agents; renaming the id would break every account_navigation row
  // keyed on it, and a silent internal rename across live scheduled work is how
  // things break at 3am.
  'automations',
  // GHOST IS NOT ON THIS LIST AND DOES NOT NEED TO BE. ONE-GHOST-2 retired the
  // nav node; Ghost is the launcher that renders on every screen, so he cannot
  // be hidden by any means available to a merchant. Naming a 'ghost' id here
  // would name a node that does not exist.
  //
]

/**
 * THE EDITOR CANNOT BE HIDDEN WITH EVERYTHING ELSE.
 *
 * The Settings SECTION being permanent is not enough. The menu editor is a
 * CHILD of it, and a child of a permanent section is hideable like any other -
 * so a merchant could hide the one row that leads to the screen where hiding
 * is undone, and the route guard would then refuse the URL too. That is the
 * one-way door the permanent list exists to prevent, one level down.
 *
 * Found by listing every route a hidden module can swallow, not by review.
 */
const PERMANENT_CHILD_IDS: ReadonlyArray<string> = [
  'settings_menu',
  // AGENTS-RENAME-1: THE SAME DOOR, ONE LEVEL DOWN, AND THE GUARD FOUND IT.
  //
  // Putting 'automations' in the permanent list above is NOT enough on its own.
  // applyAccountLayer drops a section that had children and now has none, so a
  // merchant could hide both of Agents' children and the whole section would
  // disappear while the section id sat protected and untouched.
  //
  // Connectors is safe from this without an entry because it is childless; a
  // section with no children is never dropped by that rule. Agents is the only
  // one of the four exposed to it, so its landing child is protected here.
  'automations_automations',
]

/** Every node id an account may never hide, at either level. */
export const PERMANENT_NODE_IDS: ReadonlyArray<string> = [
  ...PERMANENT_SECTION_IDS,
  ...PERMANENT_CHILD_IDS,
]

/** Every id an override may legitimately name. */
export function navNodeIds(sections: ReadonlyArray<NavSection>): string[] {
  const out: string[] = []
  for (const s of sections) {
    out.push(s.id)
    for (const c of s.children) out.push(c.id)
  }
  return out
}

const isPermanent = (id: string) => PERMANENT_NODE_IDS.includes(id)

/** Order by the merchant's sortOrder where they gave one, ours otherwise. */
function reorder<T extends { id: string }>(items: T[], config: AccountNavConfig): T[] {
  return items
    .map((item, index) => ({ item, index, order: config[item.id]?.sortOrder ?? null }))
    .sort((a, b) => {
      // A node the merchant placed comes before one they did not, and two
      // unplaced nodes keep the order we shipped.
      if (a.order === null && b.order === null) return a.index - b.index
      if (a.order === null) return 1
      if (b.order === null) return -1
      return a.order - b.order || a.index - b.index
    })
    .map((x) => x.item)
}

/**
 * Apply one account's configuration to an ALREADY ROLE-FILTERED tree.
 *
 * Subtract only. The input is the ceiling; nothing here can exceed it.
 */
export function applyAccountLayer(
  roleFiltered: ReadonlyArray<NavSection>,
  config: AccountNavConfig,
): NavSection[] {
  const sections: NavSection[] = []

  for (const section of reorder([...roleFiltered], config)) {
    const sectionOverride = config[section.id]

    // A permanent module ignores `hidden` entirely. The database refuses to
    // store it; this refuses to honour one that somehow exists.
    if (sectionOverride?.hidden && !isPermanent(section.id)) continue

    const children: NavChild[] = []
    for (const child of reorder([...section.children], config)) {
      const childOverride = config[child.id]
      if (childOverride?.hidden && !isPermanent(child.id)) continue
      children.push(childOverride?.label ? { ...child, label: childOverride.label } : child)
    }

    // A section that had children and now has none disappears with them -
    // matching filterSectionsByRole, which already drops a section whose every
    // child is gated away. A childless section (Home, Ghost) is unaffected.
    if (section.children.length > 0 && children.length === 0) continue

    const label = sectionOverride?.label ?? section.label
    sections.push(
      label === section.label && children === section.children
        ? { ...section, children }
        : { ...section, label, children },
    )
  }

  return sections
}

// ─── The three states ──────────────────────────────────────────────────
//
// GHOST-VISION law 2, at the menu. "We could not read your configuration",
// "you have not configured anything" and "here is your configuration" are
// three different situations, and the first must never look like an empty
// menu - a merchant whose navigation vanished because a read failed would
// think the product broke.

export type AccountNavState =
  | { state: 'loading' }
  | { state: 'failed'; reason: string }
  | { state: 'loaded'; config: AccountNavConfig }

export interface ResolvedNav {
  sections: NavSection[]
  /** Non-null only when the configuration could not be read. */
  warning: string | null
  /**
   * SHELF-FLASH-2 — TRUE WHILE WE DO NOT YET KNOW WHAT THIS ACCOUNT HIDES.
   *
   * Needed because `sections: []` alone is ambiguous: it is also what a role
   * with nothing visible would produce. A consumer must be able to tell "we
   * have not asked" from "we asked and there is nothing", or it will render an
   * empty menu as though that were the answer.
   */
  loading: boolean
}

export function resolveAccountNav(
  roleFiltered: ReadonlyArray<NavSection>,
  state: AccountNavState,
): ResolvedNav {
  if (state.state === 'loaded') {
    return {
      sections: applyAccountLayer(roleFiltered, state.config),
      warning: null,
      loading: false,
    }
  }
  if (state.state === 'failed') {
    // FALL BACK TO THE FULL TREE AND SAY SO. Showing everything is the safe
    // failure: a merchant sees a menu item they had hidden, which is a
    // cosmetic surprise, rather than losing a module they rely on.
    return {
      sections: applyAccountLayer(roleFiltered, {}),
      warning: `We could not read your menu settings, so this is the full menu. ${state.reason}`,
      loading: false,
    }
  }
  // SHELF-FLASH-2 — STILL LOADING RENDERS NOTHING, NOT EVERYTHING.
  //
  // This returned the FULL tree, which is how a hidden section rendered and
  // then disappeared. Showing everything is the right safe failure for a read
  // that FAILED — that branch above is untouched — but it is the wrong answer
  // for a read that has not happened, because the two are different situations
  // and only one of them is a failure.
  //
  // No sections and `loading: true`. The sidebar shows its skeleton and
  // AuthGuard shows the same, so nothing is rendered that might have to be
  // taken away, and no route is accused of being hidden before we know.
  return { sections: [], warning: null, loading: true }
}

// ─── Route ownership ───────────────────────────────────────────────────

/**
 * Which node owns a route, so a page can tell a merchant they have hidden it.
 *
 * Matched by the LONGEST route prefix, so a child beats its section and a
 * deeper child beats a shallower one. A route no node owns returns null and is
 * left ungated - reported by name rather than guessed at.
 */
/**
 * The dashboard root. A section living here owns ONLY itself.
 *
 * Home sits at /dashboard, and prefix-matching would make it the owner of
 * every route in the product - which would make "no owner" an empty set and
 * hide the routes this wave is supposed to report by name.
 */
const ROOT_PATH = '/dashboard'

/** Does `owner` own `pathname`: exactly, or as a real parent segment? */
function owns(owner: string, pathname: string): boolean {
  if (pathname === owner) return true
  if (owner === ROOT_PATH) return false
  return pathname.startsWith(owner + '/')
}

export function ownerNodeIdForPath(
  sections: ReadonlyArray<NavSection>,
  pathname: string,
): string | null {
  let bestId: string | null = null
  let bestLen = 0
  for (const s of sections) {
    for (const c of s.children) {
      if (owns(c.to, pathname) && c.to.length > bestLen) {
        bestId = c.id
        bestLen = c.to.length
      }
    }
    if (owns(s.to, pathname) && s.to.length > bestLen) {
      bestId = s.id
      bestLen = s.to.length
    }
  }
  return bestId
}

/**
 * Has this account hidden the module this route belongs to?
 *
 * Answered against the RESOLVED tree: if the owning node is not in it, the
 * merchant cannot navigate here, and a page that renders in full over an empty
 * table is lying to them about what this business uses.
 *
 * A route with no owner is never hidden. A permanent module is never hidden.
 */
export function isPathHidden(
  resolved: ReadonlyArray<NavSection>,
  allRoleVisible: ReadonlyArray<NavSection>,
  pathname: string,
): { hidden: false } | { hidden: true; nodeId: string; label: string } {
  const ownerId = ownerNodeIdForPath(allRoleVisible, pathname)
  if (!ownerId || isPermanent(ownerId)) return { hidden: false }
  const stillVisible = navNodeIds(resolved).includes(ownerId)
  if (stillVisible) return { hidden: false }

  // Its label as WE named it, because the merchant hid it and may have renamed
  // it to something they no longer see.
  for (const s of allRoleVisible) {
    if (s.id === ownerId) return { hidden: true, nodeId: ownerId, label: s.label }
    for (const c of s.children) {
      if (c.id === ownerId) return { hidden: true, nodeId: ownerId, label: c.label }
    }
  }
  return { hidden: false }
}

/**
 * SHELF-FLASH-2 — THE ROUTE'S ANSWER, WITH THE THIRD STATE THE GUARD NEEDS.
 *
 * `isPathHidden` above answers two things and is left exactly as it is, because
 * its two answers are correct once the configuration is known. What it cannot
 * say is "I do not know yet", and during that window it answers the worst
 * possible thing: `resolved` is empty, so every route looks HIDDEN and a
 * merchant is told they hid a section they never touched.
 *
 * So the guard asks this instead. `unknown` is not `hidden` and is not
 * `visible`: it renders the same skeleton the sidebar does, and the moment the
 * rows land the route resolves for real. One hop, one source of truth, and no
 * route reachable in the gap.
 */
export type PathVerdict =
  | { state: 'visible' }
  | { state: 'hidden'; nodeId: string; label: string }
  | { state: 'unknown' }

export function pathVerdict(
  nav: ResolvedNav,
  allRoleVisible: ReadonlyArray<NavSection>,
  pathname: string,
): PathVerdict {
  // THE-HOME-LOADS-FAST-1 — A ROUTE THAT CANNOT BE HIDDEN NEVER WAITS.
  //
  // `unknown` exists because a route the account MIGHT have hidden must not be
  // rendered before we know. That reason does not reach a route no menu node
  // owns, or one whose owner is on the permanent list: neither can be hidden by
  // any configuration, so the answer is already `visible` and reading the rows
  // cannot change it.
  //
  // THE HOME IS ONE OF THEM. `/dashboard` is owned by `home`, which is in
  // `PERMANENT_SECTION_IDS` and in the database's own
  // `account_navigation_permanent_not_hidden` check. So the first screen a new
  // merchant ever sees drew the sidebar's loading bars, in the middle of the
  // page, waiting on an answer that was settled before the read began. Measured
  // in a headless browser against the built app: the home's frame took 6,069 ms
  // on a mid range phone connection and this was one of its two gates.
  //
  // This is not a special case for the home and names no route: it is the
  // question `isPathHidden` already asks, asked one step earlier, where it
  // happens to be answerable without the rows.
  const owner = ownerNodeIdForPath(allRoleVisible, pathname)
  if (owner === null || isPermanent(owner)) return { state: 'visible' }
  if (nav.loading) return { state: 'unknown' }
  const answer = isPathHidden(nav.sections, allRoleVisible, pathname)
  return answer.hidden
    ? { state: 'hidden', nodeId: answer.nodeId, label: answer.label }
    : { state: 'visible' }
}
